import {
  computed,
  readonly,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import { onDispose, unrefElement, type NxMaybeElement } from './element';

export interface NxElementSizeOptions {
  /** Which box to report. Default `'content'` (inside the padding). */
  box?: 'content' | 'border';
  /** Reported until the first measurement, and for good without ResizeObserver. */
  initial?: { width: number; height: number };
}

/**
 * Live size of an element in CSS pixels, from a ResizeObserver. The element
 * may come and go (behind a `v-if`); the observer follows it. Until the first
 * measurement — and during SSR or in jsdom, which lack the observer — it
 * reports `initial` (default 0 × 0).
 */
export function useElementSize(
  target: NxMaybeElement,
  options: NxElementSizeOptions = {}
): { width: Readonly<Ref<number>>; height: Readonly<Ref<number>>; stop: () => void } {
  const initial = options.initial ?? { width: 0, height: 0 };
  const width = ref(initial.width);
  const height = ref(initial.height);
  const border = options.box === 'border';
  const observer =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver((entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          const size = border ? entry.borderBoxSize?.[0] : entry.contentBoxSize?.[0];
          if (size) {
            width.value = size.inlineSize;
            height.value = size.blockSize;
          } else {
            width.value = entry.contentRect.width;
            height.value = entry.contentRect.height;
          }
        });
  const stopWatch = watch(
    () => unrefElement(target),
    (next, prev) => {
      if (!observer) return;
      if (prev) observer.unobserve(prev);
      if (next) observer.observe(next, { box: border ? 'border-box' : 'content-box' });
    },
    { immediate: true, flush: 'post' }
  );
  const stop = () => {
    stopWatch();
    observer?.disconnect();
  };
  onDispose(stop);
  return { width: readonly(width), height: readonly(height), stop };
}

export interface NxIntersectionOptions {
  /** Scroll container to intersect with. Default: the viewport. */
  root?: NxMaybeElement;
  rootMargin?: string;
  threshold?: number | number[];
  /** Stop watching after the first hit (lazy loading). Default `false`. */
  once?: boolean;
  /** Reported without IntersectionObserver (SSR, jsdom). Default `false`. */
  fallback?: boolean;
}

/**
 * Whether an element is in view, from an IntersectionObserver. `entry` holds
 * the last full observation. Follows the element through `v-if`.
 */
export function useIntersection(
  target: NxMaybeElement,
  options: NxIntersectionOptions = {}
): {
  isIntersecting: Readonly<Ref<boolean>>;
  entry: ComputedRef<IntersectionObserverEntry | null>;
  stop: () => void;
} {
  const supported = typeof IntersectionObserver !== 'undefined';
  const isIntersecting = ref(supported ? false : (options.fallback ?? false));
  const entry = shallowRef<IntersectionObserverEntry | null>(null);
  let observer: IntersectionObserver | null = null;
  let done = false;

  const disconnect = () => {
    observer?.disconnect();
    observer = null;
  };

  const stopWatch = watch(
    () => [unrefElement(target), options.root ? unrefElement(options.root) : null] as const,
    ([el, root]) => {
      disconnect();
      if (!supported || !el || done) return;
      observer = new IntersectionObserver(
        (entries) => {
          const last = entries[entries.length - 1];
          if (!last) return;
          entry.value = last;
          isIntersecting.value = last.isIntersecting;
          if (last.isIntersecting && options.once) {
            done = true;
            disconnect();
          }
        },
        { root, rootMargin: options.rootMargin, threshold: options.threshold }
      );
      observer.observe(el);
    },
    { immediate: true, flush: 'post' }
  );
  const stop = () => {
    stopWatch();
    disconnect();
  };
  onDispose(stop);
  return { isIntersecting: readonly(isIntersecting), entry: computed(() => entry.value), stop };
}

/**
 * Whether a media query matches, kept live: `useMediaQuery('(max-width: 40rem)')`.
 * Without `matchMedia` (SSR, jsdom) it reports `initial` (default `false`).
 */
export function useMediaQuery(
  query: MaybeRefOrGetter<string>,
  options: { initial?: boolean } = {}
): Readonly<Ref<boolean>> {
  const matches = ref(options.initial ?? false);
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  let list: MediaQueryList | null = null;
  const onChange = (event: { matches: boolean }) => {
    matches.value = event.matches;
  };
  const detach = () => {
    if (!list) return;
    if (typeof list.removeEventListener === 'function')
      list.removeEventListener('change', onChange);
    else list.removeListener?.(onChange);
    list = null;
  };
  const stopWatch = watch(
    () => toValue(query),
    (q) => {
      detach();
      if (!supported || !q) return;
      list = window.matchMedia(q);
      matches.value = list.matches;
      if (typeof list.addEventListener === 'function') list.addEventListener('change', onChange);
      else list.addListener?.(onChange);
    },
    { immediate: true }
  );
  onDispose(() => {
    stopWatch();
    detach();
  });
  return readonly(matches);
}

/** Whether the reader asked the system for less motion. */
export function usePrefersReducedMotion(): Readonly<Ref<boolean>> {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * Whether the operating system is set to dark. That is the OS preference
 * only — an app theme chosen in the shell may differ.
 */
export function usePrefersDark(): Readonly<Ref<boolean>> {
  return useMediaQuery('(prefers-color-scheme: dark)');
}
